/* AthleteAvatarFallback — Phase 1 · Digital Home polish
 * ─────────────────────────────────────────────────────────────────────
 * Replaces the flat black circle we were showing for athletes without
 * a photo. Two modes:
 *
 *   1. UNCLAIMED profile (auto-generated / roster-sync)  →  a dark
 *      "Liquid Glass" panel with "CLAIM NOW" centered, subtly tinted
 *      in the school's primary color. This turns every empty roster
 *      slot into a growth-loop CTA.
 *
 *   2. CLAIMED profile without a photo  →  a soft radial fill in the
 *      school's color with the athlete's initials or sport glyph.
 *
 * Stock photos have been removed entirely — they pulled the wrong
 * sport (soccer for football, etc.) and undercut the premium feel.
 *
 * Usage:
 *   <AthleteAvatarFallback athlete={a} size={68} />
 *   <AthleteAvatarFallback athlete={a} className="w-24 h-24" />
 */
import { Circle } from 'lucide-react';

// Kept exported (as an empty object) so existing imports across the
// app continue to resolve. All stock photo URLs have been removed —
// see the file header for the rationale.
export const SCHOOL_STOCK_BG = {};

// Canonical primary colors per school (hex). Ultimate fallback for
// athletes whose school doc doesn't have a color set.
export const SCHOOL_PRIMARY_COLOR = {
  'texas-longhorns':        '#BF5700',
  'texas-am-aggies':        '#500000',
  'smu-mustangs':           '#C8102E',
  'baylor-bears':           '#154734',
  'texas-tech-red-raiders': '#CC0000',
  'tcu-horned-frogs':       '#4D1979',
};

// Small SVG glyphs by sport — inlined so the fallback works offline
// and never fetches an image.
const SPORT_GLYPHS = {
  football: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M4 13c0-4.5 3.5-8 8-8 4 0 7 2 8 5.5.5 1.5.5 3 0 4.5-.4 1.2-1.2 2-2 2h-1v2H8v-2H6c-1.2 0-2-1-2-2.5V13z"
        stroke="currentColor" strokeWidth="1.6"
        fill="currentColor" fillOpacity="0.18" strokeLinejoin="round"
      />
      <path d="M13 13h6M13 15.5h6M13 11h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="9" cy="12.5" r="1.4" fill="currentColor" opacity="0.35" />
      <path d="M8 17c1 1 2 1.5 3 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  basketball: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="currentColor" fillOpacity="0.18" />
      <path d="M3 12h18M12 3v18M5.5 5.5c4 4 9 4 13 0M5.5 18.5c4-4 9-4 13 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  baseball: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="currentColor" fillOpacity="0.18" />
      <path d="M6 6c2 2 3 4 3 6s-1 4-3 6M18 6c-2 2-3 4-3 6s1 4 3 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  soccer: (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="currentColor" fillOpacity="0.18" />
      <path d="M12 4v3M12 17v3M4 12h3M17 12h3M6 6l2 2M16 16l2 2M18 6l-2 2M6 18l2-2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
};

const guessSportKey = (sport) => {
  const s = (sport || '').toLowerCase();
  if (s.includes('football')) return 'football';
  if (s.includes('basket')) return 'basketball';
  if (s.includes('base') || s.includes('softball')) return 'baseball';
  if (s.includes('soccer') || s.includes('futbol')) return 'soccer';
  return null;
};

const initialsOf = (name) => {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
};

// iter239 · Short "logo mark" for a school — "SMU Mustangs" → SMU,
// "Duncanville High School" → DHS, "Texas Longhorns" → TL. Used as the
// centerpiece on unclaimed tiles so every school reads like a badge.
export const schoolCodeOf = (a) => {
  const src = ((a?.school_name || a?.team || '') + '').trim();
  if (!src) return '';
  const tokens = src.split(/\s+/);
  if (/^[A-Z0-9&]{2,5}$/.test(tokens[0])) return tokens[0];
  return tokens.slice(0, 3).map((t) => t[0]?.toUpperCase()).join('');
};

// An athlete is "unclaimed" ONLY when the row is a genuine roster
// import with nobody owning it yet — no `public_user_id`, no
// `auto_provisioned` flag, and either no claim_status or an explicit
// public/unclaimed/pending marker. Self-created profiles (from the
// in-app signup flow) come in with `auto_provisioned=true` + a
// `public_user_id`, so we never draw the "CLAIM NOW" liquid-glass
// overlay on the athlete's own tile.
const isUnclaimed = (a) => {
  if (!a) return false;
  if (a.public_user_id) return false;
  if (a.auto_provisioned) return false;
  if (a.verified) return false;
  const status = (a.claim_status || '').toLowerCase();
  if (status === 'self_owned' || status === 'verified' || status === 'claimed') return false;
  if (!status) return true;                                  // roster-synced default
  return status === 'public' || status === 'unclaimed' || status === 'pending';
};

const AthleteAvatarFallback = ({
  athlete,
  size = 68,
  className = '',
  showInitials = false,   // if true, use initials instead of sport glyph
  ringWidth = 2,
  // iter229e · forceInitials skips the "Claim & Verify" liquid-glass
  // overlay and always renders the initials-on-color-disk look. Used
  // on the athlete profile hero so unclaimed profiles still read as
  // that athlete (e.g. "DM" for Dre'lon Miller), not as a giant
  // Claim CTA that duplicates the banner below.
  forceInitials = false,
  // `showBackdrop` kept for backward-compat with existing call sites —
  // it no longer paints a photo (all stock imagery removed).
  showBackdrop: _showBackdrop = false, // kept for backward-compat; ignored
}) => {
  const color = athlete?.school_primary_color
    || SCHOOL_PRIMARY_COLOR[athlete?.school_slug]
    || '#3b82f6';    // Flyt blue fallback
  const sportKey = guessSportKey(athlete?.sport);
  const glyph = sportKey ? SPORT_GLYPHS[sportKey] : null;
  const initials = initialsOf(athlete?.name);
  const unclaimed = !forceInitials && isUnclaimed(athlete);

  // Compose an inline style so callers can pass either a fixed pixel
  // `size` OR use tailwind classes for responsive sizing (className).
  const dim = className ? {} : { width: size, height: size };

  // Font sizing for the "CLAIM NOW" CTA — scales with the avatar so
  // 68px tiles feel balanced and 110px hero circles read boldly.
  const claimFontPx = className
    ? undefined   // let tailwind class control the container; use fluid size
    : Math.max(size * 0.14, 9);

  return (
    <div
      className={`relative rounded-full flex items-center justify-center overflow-hidden ${className}`}
      style={{
        ...dim,
        // Deep near-black base with a whisper of the school's color at
        // the top-left so each school still reads as itself. No photos.
        background: `radial-gradient(circle at 30% 20%, ${color}33 0%, ${color}11 40%, #050505 100%)`,
        boxShadow: `inset 0 0 0 ${ringWidth}px ${color}, 0 4px 20px rgba(0,0,0,0.35)`,
      }}
      data-testid={unclaimed ? 'athlete-avatar-claim-now' : 'athlete-avatar-fallback'}
      aria-label={
        unclaimed
          ? (athlete?.name ? `${athlete.name} — profile unclaimed` : 'Unclaimed athlete profile')
          : (athlete?.name ? `${athlete.name} — no photo yet` : 'Athlete avatar')
      }
    >
      {unclaimed ? (
        <>
          {/* iter239 · School badge look (per Jay) — instead of the dark
              "Claim & Verify" glass that read as a weird black outline,
              unclaimed tiles now render as a school-colored disk with
              the school's mark (SMU / DHS / TL) in the middle. Athlete
              name renders below via the parent card. */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 35% 25%, ${color} 0%, ${color}CC 55%, ${color}66 100%)`,
            }}
          />
          {/* Soft gloss so the disk reads like a physical badge */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.05) 40%, rgba(0,0,0,0.28) 100%)',
            }}
          />
          <span
            className="relative font-display uppercase font-black tracking-[0.08em] leading-none text-white drop-shadow-lg"
            style={{ fontSize: className ? 'clamp(14px, 30%, 34px)' : `${Math.max(size * 0.28, 13)}px` }}
            data-testid="school-badge-code"
          >
            {schoolCodeOf(athlete) || initials || <Circle className="w-5 h-5 opacity-40" />}
          </span>
        </>
      ) : (
        <>
          {/* Subtle color wash */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(180deg, ${color}18 0%, transparent 60%)` }}
          />
          {/* Center content — initials or sport glyph for claimed profiles. */}
          <div
            className="relative"
            style={{ width: '55%', height: '55%', color: '#ffffff' }}
          >
            {showInitials || !glyph ? (
              <span
                className="absolute inset-0 flex items-center justify-center font-display uppercase font-black leading-none drop-shadow-lg"
                style={{ fontSize: `${(className ? 20 : Math.max(size * 0.28, 14))}px` }}
              >
                {initials || <Circle className="w-full h-full opacity-40" />}
              </span>
            ) : (
              <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full drop-shadow-lg">{glyph}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AthleteAvatarFallback;
